import React from 'react';
import Icon from '../../../components/AppIcon';

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

export const FORMATOS = [
  { value: 'ENTERO', label: 'Entero' },
  { value: '50G', label: '50 g' },
  { value: '100G', label: '100 g' },
  { value: '500G', label: '500 g' },
  { value: '1KG', label: '1 kg' },
];

export const inputCls = 'w-full bg-[#1A1A1A] border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40';
const labelCls = 'block text-white/60 text-xs font-oranienbaum mb-1.5 uppercase tracking-wide';

/**
 * TrabajoForm — componente unificado para Crear y Editar trabajos.
 *
 * Props:
 *  mode: 'create' | 'edit'
 *  form: { fecha, cantidad_piezas, producto_id, empleado_id, prioridad, tipo_corte, formato_corte, quiere_huesos, guardar_merma, observaciones }
 *  onChange: (key, value) => void
 *  productos: []
 *  empleados: []
 *  weekDates: []  (array of date strings Mon-Sat)
 *
 *  // Solo para create:
 *  selectedConfig: object | null
 *  needsProducto: bool
 *  guardarHabitual: bool
 *  onGuardarHabitual: (bool) => void
 *  clienteNombre: string
 *
 *  // Solo para edit:
 *  piezasWarning: string
 *  activePiezasCount: number
 *
 *  // Siempre:
 *  validationError: string
 *  showMasOpciones: bool
 *  onToggleMasOpciones: () => void
 */
const TrabajoForm = ({
  mode = 'create',
  form,
  onChange,
  productos = [],
  empleados = [],
  weekDates = [],
  // create-only
  selectedConfig = null,
  needsProducto = false,
  guardarHabitual = false,
  onGuardarHabitual,
  clienteNombre = '',
  // edit-only
  piezasWarning = '',
  activePiezasCount = 0,
  // shared
  validationError = '',
  showMasOpciones = false,
  onToggleMasOpciones,
}) => {
  const weekDatesSinDomingo = weekDates?.slice(0, 6);

  return (
    <div className="space-y-4">
      {/* ── Config heredada (solo create con config) ─────────────── */}
      {mode === 'create' && selectedConfig && !needsProducto && (
        <div className="p-3 rounded-xl border border-[#D6EF4E]/15 bg-[#D6EF4E]/5">
          <p className="text-[#D6EF4E]/80 text-xs font-oranienbaum font-semibold mb-1">✓ Configuración habitual cargada</p>
          <div className="text-white/50 text-xs font-oranienbaum space-y-0.5">
            {selectedConfig?.producto?.nombre && <div>Producto: {selectedConfig?.producto?.nombre}</div>}
            {selectedConfig?.tipo_corte && <div>Corte: {selectedConfig?.tipo_corte}</div>}
            {selectedConfig?.formato_corte && selectedConfig?.formato_corte !== 'ENTERO' && (
              <div>Formato: {selectedConfig?.formato_corte?.replace('G', 'g')?.replace('KG', 'kg')}</div>
            )}
            {selectedConfig?.quiere_huesos && <div>🦴 Guardar huesos</div>}
            {selectedConfig?.guardar_merma && <div>♻ Guardar merma</div>}
          </div>
        </div>
      )}

      {/* ── Selector de producto (create sin producto habitual) ───── */}
      {mode === 'create' && needsProducto && (
        <div>
          <label className="block text-white/70 text-sm font-oranienbaum mb-2">
            Producto <span className="text-red-400">*</span>
          </label>
          <select
            value={form?.producto_id || ''}
            onChange={e => onChange('producto_id', e?.target?.value)}
            className={inputCls}
            style={{ colorScheme: 'dark' }}
          >
            <option value="">Seleccionar producto...</option>
            {productos?.map(p => (
              <option key={p?.id} value={p?.id}>{p?.nombre}</option>
            ))}
          </select>
          {form?.producto_id && (
            <label className="flex items-center gap-2 mt-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={guardarHabitual}
                onChange={e => onGuardarHabitual?.(e?.target?.checked)}
                className="w-4 h-4 rounded border-white/20 bg-[#1A1A1A] accent-[#D6EF4E] cursor-pointer"
              />
              <span className="text-white/50 text-xs font-oranienbaum group-hover:text-white/70 transition-colors">
                Guardar este producto como habitual para {clienteNombre}
              </span>
            </label>
          )}
        </div>
      )}

      {/* ── Día / Fecha ───────────────────────────────────────────── */}
      <div>
        <label className={labelCls}>Día / Fecha</label>
        {weekDatesSinDomingo?.length > 0 ? (
          <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
            {weekDatesSinDomingo?.map((d, i) => (
              <button
                key={d}
                type="button"
                onClick={() => onChange('fecha', d)}
                className={`py-2.5 rounded-lg text-xs font-oranienbaum border transition-colors ${
                  form?.fecha === d
                    ? 'bg-[#D6EF4E]/15 border-[#D6EF4E]/40 text-[#D6EF4E]'
                    : 'border-white/10 text-white/50 hover:border-white/25'
                }`}
              >
                {DIAS_SEMANA?.[i]?.slice(0, 3)}
              </button>
            ))}
          </div>
        ) : (
          <input
            type="date"
            value={form?.fecha || ''}
            onChange={e => onChange('fecha', e?.target?.value)}
            className={inputCls}
          />
        )}
      </div>

      {/* ── Cantidad de piezas ────────────────────────────────────── */}
      <div>
        <label className={labelCls}>Cantidad de piezas</label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onChange('cantidad_piezas', Math.max(1, (form?.cantidad_piezas || 1) - 1))}
            className="w-11 h-11 rounded-lg bg-white/8 text-white/70 hover:bg-white/12 font-bold text-lg transition-colors"
          >−</button>
          <span className="text-white font-oranienbaum font-bold text-2xl w-12 text-center">{form?.cantidad_piezas || 1}</span>
          <button
            type="button"
            onClick={() => onChange('cantidad_piezas', (form?.cantidad_piezas || 1) + 1)}
            className="w-11 h-11 rounded-lg bg-white/8 text-white/70 hover:bg-white/12 font-bold text-lg transition-colors"
          >+</button>
        </div>
        {mode === 'edit' && activePiezasCount > 0 && (
          <p className="text-white/40 text-xs font-oranienbaum mt-1.5">
            {activePiezasCount} piezas activas (no se pueden eliminar)
          </p>
        )}
        {mode === 'edit' && piezasWarning && (
          <p className="text-amber-400/70 text-xs font-oranienbaum mt-1.5">⚠ {piezasWarning}</p>
        )}
      </div>

      {/* ── Botón Más opciones ────────────────────────────────────── */}
      <button
        type="button"
        onClick={onToggleMasOpciones}
        className="w-full py-2.5 rounded-lg border border-white/10 text-white/40 text-xs font-oranienbaum hover:border-white/20 hover:text-white/60 transition-colors flex items-center justify-center gap-1.5"
      >
        <Icon name={showMasOpciones ? 'ChevronUp' : 'ChevronDown'} size={12} />
        {showMasOpciones ? 'Menos opciones' : 'Más opciones'}
      </button>

      {/* ── Más opciones expandido ────────────────────────────────── */}
      {showMasOpciones && (
        <div className="space-y-4 pt-2 border-t border-white/5">

          {/* Producto — edit siempre visible; create solo si tiene config (override) */}
          {(mode === 'edit' || (mode === 'create' && selectedConfig)) && (
            <div>
              <label className={labelCls}>
                {mode === 'edit' ? 'Producto' : 'Cambiar producto (solo este trabajo)'}
              </label>
              <select
                value={form?.producto_id || ''}
                onChange={e => onChange('producto_id', e?.target?.value)}
                className={inputCls}
                style={{ colorScheme: 'dark' }}
              >
                {mode === 'create' && (
                  <option value="">Usar habitual ({selectedConfig?.producto?.nombre || 'Sin especificar'})</option>
                )}
                {mode === 'edit' && <option value="">Seleccionar producto...</option>}
                {productos?.map(p => (
                  <option key={p?.id} value={p?.id}>{p?.nombre}</option>
                ))}
              </select>
            </div>
          )}

          {/* Empleado */}
          <div>
            <label className={labelCls}>Empleado</label>
            <select
              value={form?.empleado_id || ''}
              onChange={e => onChange('empleado_id', e?.target?.value)}
              className={inputCls}
              style={{ colorScheme: 'dark' }}
            >
              <option value="">Sin asignar</option>
              {empleados?.map(e => <option key={e?.id} value={e?.id}>{e?.nombre}</option>)}
            </select>
          </div>

          {/* Tipo de corte + Prioridad */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Tipo de corte</label>
              <select
                value={form?.tipo_corte || 'NORMAL'}
                onChange={e => onChange('tipo_corte', e?.target?.value)}
                className={inputCls}
                style={{ colorScheme: 'dark' }}
              >
                <option value="NORMAL">Normal</option>
                <option value="EMPLATADO">Emplatado</option>
                <option value="PALETILLA">Paletilla</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Prioridad</label>
              <div className="flex gap-1.5">
                {[{ v: 'NORMAL', l: 'Normal' }, { v: 'URGENTE', l: '🔴 Urgente' }]?.map(({ v, l }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => onChange('prioridad', v)}
                    className={`flex-1 py-2 rounded-lg text-xs font-oranienbaum border transition-colors ${
                      (form?.prioridad || 'NORMAL') === v
                        ? v === 'URGENTE' ? 'bg-red-500/15 border-red-500/30 text-red-400' : 'bg-[#D6EF4E]/15 border-[#D6EF4E]/40 text-[#D6EF4E]'
                        : 'border-white/10 text-white/50 hover:border-white/20'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Formato */}
          <div>
            <label className={labelCls}>Formato de corte / Envasado</label>
            <div className="flex gap-1.5 flex-wrap">
              {FORMATOS?.map(f => (
                <button
                  key={f?.value}
                  type="button"
                  onClick={() => onChange('formato_corte', f?.value)}
                  className={`px-3 py-2 rounded-lg text-xs font-oranienbaum border transition-colors ${
                    (form?.formato_corte || 'ENTERO') === f?.value
                      ? 'bg-[#D6EF4E]/15 border-[#D6EF4E]/40 text-[#D6EF4E]' :'border-white/10 text-white/50 hover:border-white/20'
                  }`}
                >
                  {f?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Huesos + Merma */}
          <div className="flex gap-5 flex-wrap">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <div
                onClick={() => onChange('quiere_huesos', !form?.quiere_huesos)}
                className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                  form?.quiere_huesos ? 'bg-amber-400 border-amber-400' : 'border-white/20 hover:border-white/40'
                }`}
              >
                {form?.quiere_huesos && <Icon name="Check" size={13} className="text-black" />}
              </div>
              <span className="text-white/70 text-sm font-oranienbaum">🦴 Quiere huesos</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <div
                onClick={() => onChange('guardar_merma', !form?.guardar_merma)}
                className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                  form?.guardar_merma ? 'bg-green-400 border-green-400' : 'border-white/20 hover:border-white/40'
                }`}
              >
                {form?.guardar_merma && <Icon name="Check" size={13} className="text-black" />}
              </div>
              <span className="text-white/70 text-sm font-oranienbaum">♻ Guardar merma</span>
            </label>
          </div>

          {/* Observaciones */}
          <div>
            <label className={labelCls}>Observaciones / Instrucciones</label>
            <textarea
              value={form?.observaciones || ''}
              onChange={e => onChange('observaciones', e?.target?.value)}
              rows={2}
              placeholder="ej: Cliente recoge a las 13:00, separar puntas..."
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>
      )}

      {/* ── Error de validación ───────────────────────────────────── */}
      {validationError && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <Icon name="AlertCircle" size={14} className="text-red-400 flex-shrink-0" />
          <span className="text-red-400 text-xs font-oranienbaum">{validationError}</span>
        </div>
      )}
    </div>
  );
};

export default TrabajoForm;
